import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Link, useParams } from 'react-router-dom';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';
import { getBlogCategories, getBlogPost, getBlogPosts } from '../api/client';
import { BlogCategory, BlogPostInfo } from '../api/types';
import '../assets/styles/Blog.scss';
import { useLanguage } from '../i18n/LanguageContext';
import remarkAlerts from './remarkAlerts';

function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const { t, lang } = useLanguage();
  const [post, setPost] = useState<BlogPostInfo | null>(null);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [allPosts, setAllPosts] = useState<BlogPostInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    Promise.all([getBlogPost(slug), getBlogCategories(), getBlogPosts()])
      .then(([postRes, categoriesRes, postsRes]) => {
        setPost(postRes);
        setCategories(categoriesRes);
        setAllPosts(postsRes);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [slug]);

  const sortedPosts = useMemo(() => {
    return [...allPosts].sort((a, b) => {
      const ad = a.published_at ? new Date(a.published_at).getTime() : 0;
      const bd = b.published_at ? new Date(b.published_at).getTime() : 0;
      return bd - ad;
    });
  }, [allPosts]);

  const currentIndex = sortedPosts.findIndex((p) => p.slug === slug);
  const previousPost = currentIndex >= 0 && currentIndex < sortedPosts.length - 1 ? sortedPosts[currentIndex + 1] : null;
  const nextPost = currentIndex > 0 ? sortedPosts[currentIndex - 1] : null;

  if (loading) return <div className="page blog-post-container"><p className="blog-status">{t.blog.loading}</p></div>;
  if (error || !post) return <div className="page blog-post-container"><p className="blog-status">{t.blog.noPostsFound}</p></div>;

  const title = lang === 'en' ? post.title_en : post.title_tr || post.title_en;
  const content = lang === 'en' ? post.content_en : post.content_tr || post.content_en;
  const category = categories.find((c) => c.slug === post.category);
  const categoryLabel = category ? (lang === 'en' ? category.name_en : category.name_tr) : post.category;

  return (
    <article className="page blog-post-container">
      <Link to="/blog" className="blog-back-link">
        <ChevronLeft size={13} />
        {t.blog.backToBlog}
      </Link>
      <h1>{title}</h1>
      <div className="blog-post-meta">
        <span className="blog-card-category">{categoryLabel}</span>
        {post.published_at && (
          <span> · {new Date(post.published_at).toLocaleDateString(lang === 'en' ? 'en-US' : 'tr-TR')}</span>
        )}
        {post.tags.length > 0 && <span> · {post.tags.join(', ')}</span>}
      </div>
      <div className="blog-post-divider" />
      <div className="blog-post-content prose">
        <ReactMarkdown
          remarkPlugins={[remarkGfm, remarkAlerts]}
          components={{
            code(props) {
              const { children, className, ref, ...rest } = props;
              const match = /language-(\w+)/.exec(className || '');
              return match ? (
                <SyntaxHighlighter
                  PreTag="div"
                  language={match[1]}
                  style={oneDark}
                >
                  {String(children).replace(/\n$/, '')}
                </SyntaxHighlighter>
              ) : (
                <code {...rest} className={className}>
                  {children}
                </code>
              );
            },
          }}
        >
          {content}
        </ReactMarkdown>
      </div>

      {(previousPost || nextPost) && (
        <nav className="blog-post-nav">
          {previousPost ? (
            <Link className="blog-post-nav-link" to={`/blog/${previousPost.slug}`}>
              <span className="blog-post-nav-label">
                <ChevronLeft size={12} />
                {t.blog.previous}
              </span>
              <span className="blog-post-nav-title">
                {lang === 'en' ? previousPost.title_en : previousPost.title_tr || previousPost.title_en}
              </span>
            </Link>
          ) : (
            <span className="blog-post-nav-spacer" />
          )}
          {nextPost ? (
            <Link className="blog-post-nav-link blog-post-nav-link-right" to={`/blog/${nextPost.slug}`}>
              <span className="blog-post-nav-label">
                {t.blog.next}
                <ChevronRight size={12} />
              </span>
              <span className="blog-post-nav-title">
                {lang === 'en' ? nextPost.title_en : nextPost.title_tr || nextPost.title_en}
              </span>
            </Link>
          ) : (
            <span className="blog-post-nav-spacer" />
          )}
        </nav>
      )}
    </article>
  );
}

export default BlogPost;
